/**
 * WhatsApp Commerce: the sales channel (connection, catalog, orders, analytics,
 * settings). Campaigns keep their own `whatsapp` namespace. Conventions: see
 * `common.ts`.
 */
const whatsappChannel: Record<string, string> = {
  // --- shell ---------------------------------------------------------------
  'shell.title': 'WhatsApp Commerce',
  'shell.subtitle': 'Sell, chat and follow up with customers on WhatsApp.',
  'shell.tabs_label': 'WhatsApp areas',
  'shell.tabs.overview': 'Overview',
  'shell.tabs.connect': 'Connect',
  'shell.tabs.catalog': 'Catalog',
  'shell.tabs.inbox': 'Inbox',
  'shell.tabs.orders': 'Orders',
  'shell.tabs.automations': 'Automations',
  'shell.tabs.templates': 'Templates',
  'shell.tabs.analytics': 'Analytics',
  'shell.tabs.settings': 'Settings',
  'shell.tabs.campaigns': 'Campaigns',
  'shell.locked': 'Not included in your plan',

  'status.NOT_CONNECTED': 'Not connected',
  'status.CONNECTING': 'Connecting',
  'status.CONNECTED': 'Connected',
  'status.AUTH_REQUIRED': 'Needs re-authorisation',
  'status.CONFIG_INCOMPLETE': 'Setup incomplete',
  'status.DISCONNECTED': 'Disconnected',
  'status.ERROR': 'Error',

  'common.loading': 'Loading…',
  'common.load_failed': 'Could not load WhatsApp data.',
  'common.generic_error': 'Something went wrong. Please try again.',
  'common.not_connected': 'Connect WhatsApp to use this.',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.see_plans': 'See plans',

  // --- overview ------------------------------------------------------------
  'overview.kpi.open_conversations': 'Open chats',
  'overview.kpi.unread': 'Unread',
  'overview.kpi.orders_today': 'Orders today',
  'overview.kpi.revenue_today': 'Revenue today',
  'overview.kpi.pending_sync': 'Products pending sync',
  'overview.kpi.failed_sync': 'Products failed to sync',
  'overview.kpi.templates_approved': 'Approved templates',
  'overview.kpi.automations_enabled': 'Automations on',
  'overview.connection.title': 'Connection',
  'overview.connection.connected': 'Messages go out from {phone}.',
  'overview.connection.not_connected':
    'Link your WhatsApp Business number to start selling in chat.',
  'overview.connection.attention': 'Your connection needs attention before messages can be sent.',
  'overview.connection.connect': 'Connect WhatsApp',
  'overview.connection.fix': 'Fix connection',
  'overview.connection.reconnect': 'Reconnect',
  'overview.connection.manage': 'Manage connection',
  'overview.setup.title': 'Setup checklist',
  'overview.setup.description': 'Everything that has to be in place before the channel earns.',
  'overview.setup.done': 'Done',
  'overview.setup.pending': 'To do',
  'overview.setup.complete': 'All set — your channel is ready.',
  'overview.quick_links.title': 'Go to',
  'overview.catalog.none_linked':
    'No catalog is linked to WhatsApp yet, so your products cannot be shown in chats.',
  'overview.catalog.problem':
    'WhatsApp cannot use your catalog. Open the Connect page and run the health check to see why.',
  'overview.catalog.link': 'Link a catalog',
  'overview.locked.inbox': 'The inbox is not included in your plan.',
  'overview.locked.automations': 'Automations are not included in your plan.',
  'overview.locked.analytics': 'WhatsApp analytics are not included in your plan.',

  // --- connect -------------------------------------------------------------
  'connect.title': 'Connect WhatsApp',
  'connect.subtitle': 'Link a WhatsApp Business number through Meta, step by step.',
  'connect.read_only':
    'Only people who manage the WhatsApp channel can connect, refresh or disconnect it. Ask an owner or admin of this business.',
  'connect.step_of': 'Step {step} of {total}',
  'connect.steps.AUTHENTICATE': 'Sign in with Meta',
  'connect.steps.SELECT_BUSINESS': 'Choose a business',
  'connect.steps.SELECT_WABA': 'Choose a WhatsApp account',
  'connect.steps.SELECT_PHONE': 'Choose a number',
  'connect.steps.REGISTER': 'Register the number',
  'connect.steps.BUSINESS_PROFILE': 'Business profile',
  'connect.steps.WEBHOOKS': 'Webhooks',
  'connect.steps.CATALOG_SYNC': 'Catalog',
  'connect.steps.TEST': 'Test',
  'connect.steps.DONE': 'Done',
  'connect.embedded.title': 'Continue with Facebook',
  'connect.embedded.description':
    'The quickest route: sign in to Meta, pick your number, and we take care of the rest.',
  'connect.embedded.button': 'Continue with Facebook',
  'connect.embedded.loading': 'Opening Meta…',
  'connect.embedded.unavailable':
    'The Meta sign-in could not be loaded — a blocker or a slow network is the usual cause. Turn off any blocker for this page and try again.',
  'connect.embedded.cancelled': 'The Meta sign-in was closed before it finished.',
  'connect.embedded.not_configured': 'Embedded signup is not configured on this platform yet.',
  'connect.unavailable':
    'Connecting WhatsApp is not available on this platform yet. Please contact support.',
  'connect.manual.title': 'Connect with a token (assisted setup)',
  'connect.manual.description':
    'For development and support-assisted setup. The token must be issued through this platform’s Meta app — a token from your own Meta app will connect but never receive messages. It is stored encrypted and never shown again.',
  'connect.manual.access_token': 'Permanent access token',
  'connect.manual.access_token_hint': 'A system-user token with WhatsApp permissions.',
  'connect.manual.phone_number_id': 'Phone number ID',
  'connect.manual.waba_id': 'WhatsApp Business Account ID',
  'connect.manual.business_id': 'Meta Business ID (optional)',
  'connect.manual.submit': 'Connect',
  'connect.manual.required': 'Token, phone number ID and WABA ID are all required.',
  'connect.manual.id_not_number':
    'This should be a number, like 123456789012345 — not a name.',
  'connect.manual.api_setup_hint': 'In your Meta app under WhatsApp → API Setup.',
  'connect.manual.business_id_hint':
    'Business Settings → Business info. A number, not the business name.',
  'connect.select_business.title': 'Which business?',
  'connect.select_business.description': 'The Meta business that owns your WhatsApp account.',
  'connect.select_business.empty': 'No businesses were found on this Meta account.',
  'connect.select_waba.title': 'Which WhatsApp account?',
  'connect.select_waba.description': 'The WhatsApp Business Account to sell from.',
  'connect.select_waba.empty': 'No WhatsApp Business Accounts were found for this business.',
  'connect.select_phone.title': 'Which number?',
  'connect.select_phone.description': 'Customers will message this number.',
  'connect.select_phone.empty': 'No phone numbers were found on this account.',
  'connect.select.continue': 'Continue',
  'connect.register.title': 'Register the number for the Cloud API',
  'connect.register.description':
    'WhatsApp has to register this number for the Cloud API before it can send messages. Enter the number’s two-step verification PIN — the one set in WhatsApp Manager, or any six digits to set one now. It is sent to Meta once and never stored.',
  'connect.register.pin': 'Two-step verification PIN',
  'connect.register.pin_hint': 'Six digits. Forgotten it? Reset it in WhatsApp Manager under the number’s settings.',
  'connect.register.pin_invalid': 'Enter the six-digit PIN.',
  'connect.register.submit': 'Register number',
  'connect.profile.title': 'Business profile',
  'connect.profile.description': 'What customers see when they open your WhatsApp profile.',
  'connect.profile.about': 'About',
  'connect.profile.address': 'Address',
  'connect.profile.description_field': 'Description',
  'connect.profile.email': 'Email',
  'connect.profile.websites': 'Websites',
  'connect.profile.websites_hint': 'One per line.',
  'connect.profile.vertical': 'Industry',
  'connect.profile.vertical_placeholder': 'Choose an industry',
  'connect.profile.verticals.apparel': 'Clothing and apparel',
  'connect.profile.verticals.beauty': 'Beauty, spa and salon',
  'connect.profile.verticals.grocery': 'Grocery',
  'connect.profile.verticals.retail': 'Shopping and retail',
  'connect.profile.verticals.restaurant': 'Restaurant',
  'connect.profile.verticals.health': 'Medical and health',
  'connect.profile.verticals.edu': 'Education',
  'connect.profile.verticals.auto': 'Automotive',
  'connect.profile.verticals.entertain': 'Entertainment',
  'connect.profile.verticals.event_plan': 'Event planning and service',
  'connect.profile.verticals.finance': 'Finance and banking',
  'connect.profile.verticals.hotel': 'Hotel and lodging',
  'connect.profile.verticals.travel': 'Travel and transportation',
  'connect.profile.verticals.prof_services': 'Professional services',
  'connect.profile.verticals.nonprofit': 'Non-profit',
  'connect.profile.verticals.govt': 'Public service',
  'connect.profile.verticals.alcohol': 'Alcoholic beverages',
  'connect.profile.verticals.otc_drugs': 'Over-the-counter drugs',
  'connect.profile.verticals.online_gambling': 'Online gambling and gaming',
  'connect.profile.verticals.physical_gambling': 'Non-online gambling and gaming',
  'connect.profile.verticals.not_a_biz': 'Not a business',
  'connect.profile.verticals.other': 'Other',
  'connect.profile.save': 'Save and continue',
  'connect.profile.skip': 'Skip for now',
  'connect.webhooks.title': 'Webhooks',
  'connect.webhooks.description':
    'Let WhatsApp deliver your customers’ messages and delivery receipts to your store. It takes one click.',
  'connect.webhooks.subscribe': 'Subscribe',
  'connect.webhooks.subscribed': 'Subscribed on {when}',
  'connect.catalog.title': 'Catalog',
  'connect.catalog.description':
    'Your products are published to a Meta catalog so customers can browse and order inside WhatsApp.',
  'connect.catalog.existing': 'Use an existing catalog ID',
  'connect.catalog.catalog_id': 'Catalog ID',
  'connect.catalog.id_not_number':
    'The catalog ID is a number from Commerce Manager → Catalog → Settings, not the catalog name.',
  'connect.catalog.create': 'Create a new catalog',
  'connect.catalog.name': 'Catalog name',
  'connect.catalog.connect': 'Connect catalog',
  'connect.catalog.skip': 'Continue without a catalog',
  'connect.catalog.auto_running': 'Setting up your catalog…',
  'connect.catalog.auto_running_hint':
    'We are creating a WhatsApp catalog for your store and linking it. This takes a few seconds.',
  'connect.catalog.auto_failed_title': 'We could not set up your catalog automatically',
  'connect.catalog.manual_hint':
    'Enter a catalog ID from Commerce Manager, or create one here and we will link it.',
  'connect.catalog.retry': 'Try again automatically',
  'connect.catalog.looking_up': 'Looking up the catalogs your Meta sign-in can use…',
  'connect.catalog.pick': 'Use one of your catalogs',
  'connect.catalog.attached': 'Connected at Meta',
  'connect.catalog.not_online_products':
    'Not an Online products catalog, so WhatsApp can’t use it. Create one with the type Online products in Commerce Manager, then press Check again.',
  'connect.catalog.none_found':
    'No catalog was found for this sign-in. Create one in Commerce Manager, connect it in WhatsApp Manager → Catalog, then press “Check again”.',
  'connect.catalog.check_again': 'Check again',
  'connect.catalog.choose': 'Choose a catalog',
  'connect.catalog.change': 'Link a different catalog',
  'connect.catalog.needs_attention': 'Your catalog needs attention.',
  'connect.test.title': 'Test the connection',
  'connect.test.description':
    'We check the token, the phone number, the webhook subscription and the catalog.',
  'connect.test.run': 'Run the test',
  'connect.test.running': 'Testing…',
  'connect.test.success': 'Everything checks out. WhatsApp is connected.',
  'connect.test.failed': 'Something is not right yet. Fix the problems below and test again.',
  'connect.connected.title': 'WhatsApp is connected',
  'connect.connected.description': 'Messages go out from {phone}.',
  'connect.connected.number': 'Number',
  'connect.connected.verified_name': 'Verified name',
  'connect.connected.quality': 'Quality rating',
  'connect.connected.tier': 'Messaging limit',
  'connect.connected.waba': 'WhatsApp Business Account',
  'connect.connected.catalog': 'Catalog',
  'connect.connected.connected_at': 'Connected on',
  'connect.connected.catalog_missing_title': 'No product catalog yet',
  'connect.connected.catalog_missing_description':
    'Your products are not sent to WhatsApp until a catalog is linked. We can create one for your store now.',
  'connect.connected.catalog_setup': 'Set up catalog',
  'connect.connected.none': '—',
  'connect.actions.health': 'Run health check',
  'connect.actions.refresh': 'Refresh details',
  'connect.actions.reconnect': 'Reconnect',
  'connect.actions.disconnect': 'Disconnect',
  'connect.actions.disconnect_confirm':
    'Disconnect WhatsApp? Messages stop until you connect again. Chats and customers are kept.',
  'connect.actions.cancel': 'Cancel setup',
  'connect.actions.cancel_confirm': 'Cancel the setup? The token you entered is discarded.',
  'connect.needs_attention.title': 'Your connection needs attention',
  'connect.needs_attention.description':
    'Re-authorise with Meta to keep sending. Chats and settings are untouched.',
  'connect.notice.subscribed': 'Webhooks subscribed.',
  'connect.notice.registered': 'Number registered for the Cloud API.',
  'connect.notice.profile_saved': 'Profile saved.',
  'connect.notice.catalog_connected': 'Catalog connected. The first sync has started.',
  'connect.notice.catalog_created': 'Catalog created and linked.',
  'connect.notice.catalog_linked': 'Your existing catalog was linked.',
  'connect.notice.catalog_recorded':
    'Catalog connected. The first sync starts when the connection test passes.',
  'connect.notice.disconnected': 'WhatsApp disconnected.',
  'connect.notice.reconnecting': 'Sign in again to reconnect.',
  'connect.notice.refreshed': 'Details refreshed.',
  'connect.notice.cancelled': 'Setup cancelled.',
  'connect.notice.health_ok': 'Health check passed.',
  'connect.notice.health_problems': 'Health check found problems.',

  'health.title': 'Health',
  'health.token_valid': 'Access token',
  'health.phone_ok': 'Phone number',
  'health.registered': 'Cloud API registration',
  'health.webhook_ok': 'Webhooks',
  'health.catalog_ok': 'Catalog',
  'health.ok': 'OK',
  'health.problem': 'Problem',
  'health.unknown': 'Not checked',
  'health.checked_at': 'Checked {when}',
  'health.problems': 'Problems',

  'qr.title': 'QR code',
  'qr.description': 'Customers scan it to open a chat with your number.',
  'qr.image_alt': 'QR code for {name} on WhatsApp',
  'qr.opens': 'Scanning opens',
  'qr.download_svg': 'Download SVG',
  'qr.print': 'Print',
  'qr.copy_link': 'Copy link',

  // --- catalog -------------------------------------------------------------
  'catalog.title': 'Catalog',
  'catalog.subtitle': 'Your products, as WhatsApp shows them.',
  'catalog.sync.title': 'Sync',
  'catalog.sync.no_catalog': 'No catalog is connected yet. Connect one from the Connect tab.',
  'catalog.sync.go_connect': 'Go to Connect',
  'catalog.sync.status.IDLE': 'Idle',
  'catalog.sync.status.RUNNING': 'Syncing',
  'catalog.sync.status.FAILED': 'Failed',
  'catalog.sync.last_sync': 'Last sync {when}',
  'catalog.sync.never': 'Never synced',
  'catalog.sync.synced': 'Synced',
  'catalog.sync.pending': 'Pending',
  'catalog.sync.failed': 'Failed',
  'catalog.sync.removed': 'Removed',
  'catalog.sync.total_products': 'Products',
  'catalog.sync.empty_at_meta':
    'WhatsApp is not holding any of your products yet, so they cannot be shown in chats. Check that your products have an image and a price, then sync again.',
  'catalog.sync.last_verified':
    'Checked with WhatsApp {when} - {count} products held',
  'catalog.sync.last_error': 'Last error',
  'catalog.sync.auto_sync': 'Keep in sync automatically',
  'catalog.sync.auto_sync_hint': 'Product changes are pushed to WhatsApp as they happen.',
  'catalog.sync.full': 'Sync everything',
  'catalog.sync.incremental': 'Sync changes',
  'catalog.sync.in_progress': 'A sync is already running.',
  'catalog.sync.queued': 'Sync started.',
  'catalog.sync.job': 'Job {kind}: {status}',
  'catalog.table.product': 'Product',
  'catalog.table.sku': 'SKU',
  'catalog.table.price': 'Price',
  'catalog.table.status': 'Status',
  'catalog.table.last_synced': 'Last synced',
  'catalog.table.resync': 'Resync',
  'catalog.table.remove': 'Remove',
  'catalog.table.remove_confirm': 'Remove {name} from the WhatsApp catalog?',
  'catalog.table.empty': 'No products match.',
  'catalog.table.search': 'Search products',
  'catalog.table.search_placeholder': 'Search by name or SKU',
  'catalog.table.status_filter': 'Status',
  'catalog.table.status_all': 'All statuses',
  'catalog.mapping.PENDING': 'Pending',
  'catalog.mapping.SYNCED': 'Synced',
  'catalog.mapping.FAILED': 'Failed',
  'catalog.mapping.REMOVED': 'Removed',
  'catalog.notice.resync_queued': 'Resync queued.',
  'catalog.notice.removed': 'Removed from the catalog.',

  // --- orders --------------------------------------------------------------
  'orders.title': 'WhatsApp orders',
  'orders.subtitle': 'Orders placed in chat or from a WhatsApp checkout link.',
  'orders.table.order': 'Order',
  'orders.table.customer': 'Customer',
  'orders.table.phone': 'Phone',
  'orders.table.mode': 'Placed via',
  'orders.table.total': 'Total',
  'orders.table.status': 'Status',
  'orders.table.payment': 'Payment',
  'orders.table.date': 'Date',
  'orders.mode.CHAT': 'Chat',
  'orders.mode.WEB': 'Web checkout',
  'orders.search': 'Search orders',
  'orders.search_placeholder': 'Order number, name or phone',
  'orders.empty_title': 'No WhatsApp orders yet',
  'orders.empty_description': 'Orders customers place in chat will show up here.',
  'orders.view_conversation': 'View WhatsApp conversation',

  // --- analytics -----------------------------------------------------------
  'analytics.title': 'WhatsApp analytics',
  'analytics.subtitle': 'How the channel is performing over the selected window.',
  'analytics.range.today': 'Today',
  'analytics.range.yesterday': 'Yesterday',
  'analytics.range.7d': '7 days',
  'analytics.range.30d': '30 days',
  'analytics.range.custom': 'Custom',
  'analytics.range.from': 'From',
  'analytics.range.to': 'To',
  'analytics.kpi.conversations': 'Conversations',
  'analytics.kpi.new_customers': 'New customers',
  'analytics.kpi.products_shared': 'Products shared',
  'analytics.kpi.product_interactions': 'Product views',
  'analytics.kpi.add_to_cart': 'Added to cart',
  'analytics.kpi.checkout_started': 'Checkouts started',
  'analytics.kpi.orders': 'Orders',
  'analytics.kpi.conversion_rate': 'Conversion',
  'analytics.kpi.revenue': 'Revenue',
  'analytics.kpi.average_order_value': 'Average order',
  'analytics.kpi.abandoned_carts': 'Abandoned carts',
  'analytics.kpi.messages_sent': 'Messages sent',
  'analytics.chart.title': 'Over time',
  'analytics.chart.description': 'Orders, revenue, conversations and messages per day.',
  'analytics.chart.orders': 'Orders',
  'analytics.chart.revenue': 'Revenue',
  'analytics.chart.conversations': 'Conversations',
  'analytics.chart.messages_sent': 'Messages sent',
  'analytics.top_products.title': 'Top products',
  'analytics.top_products.description': 'Most viewed and ordered in chat.',
  'analytics.top_products.empty': 'Nothing yet in this window.',
  'analytics.top_products.detail': '{interactions} views · {orders} orders',
  'analytics.revenue_by_channel.title': 'Revenue by channel',
  'analytics.revenue_by_channel.description': 'Where the money came from, across every channel.',
  'analytics.revenue_by_channel.empty': 'No revenue in this window.',
  'analytics.revenue_by_channel.orders': '{orders} orders',
  'analytics.funnel.title': 'Message delivery',
  'analytics.funnel.description': 'Of everything sent in this window.',
  'analytics.funnel.sent': 'Sent',
  'analytics.funnel.delivered': 'Delivered',
  'analytics.funnel.read': 'Read',
  'analytics.funnel.failed': 'Failed',
  'analytics.funnel.delivery_rate': 'Delivery rate',
  'analytics.automations.title': 'Automations',
  'analytics.automations.description': 'Automatic messages sent in this window.',
  'analytics.automations.empty': 'No automations fired.',
  'analytics.automations.sent': '{sent} sent',
  'analytics.automations.failed': '{failed} failed',

  // --- settings ------------------------------------------------------------
  'settings.title': 'WhatsApp settings',
  'settings.subtitle': 'How the channel behaves on your storefront and in chat.',
  'settings.read_only': 'You can view these settings but not change them.',
  'settings.entry_points.title': 'Storefront entry points',
  'settings.entry_points.description': 'Where shoppers on your website can jump into WhatsApp.',
  'settings.entry_points.buy_on_whatsapp': 'Buy on WhatsApp',
  'settings.entry_points.buy_on_whatsapp_hint': 'A button on every product page.',
  'settings.entry_points.chat_on_whatsapp': 'Chat button',
  'settings.entry_points.chat_on_whatsapp_hint': 'A floating chat button on every page.',
  'settings.entry_points.share_on_whatsapp': 'Share on WhatsApp',
  'settings.entry_points.share_on_whatsapp_hint': 'A share link on product pages.',
  'settings.entry_points.help_on_cart_checkout': 'Help on cart and checkout',
  'settings.entry_points.help_on_cart_checkout_hint': '"Need help? Chat on WhatsApp" near the checkout button.',
  'settings.entry_points.continue_checkout_on_whatsapp': 'Continue checkout on WhatsApp',
  'settings.entry_points.continue_checkout_on_whatsapp_hint':
    'Lets a shopper hand their cart to a chat and finish there.',
  'settings.messages.title': 'Messages',
  'settings.messages.description': 'The words customers see first.',
  'settings.messages.welcome_message': 'Welcome message',
  'settings.messages.welcome_message_hint': '{{store_name}} is replaced with your shop name.',
  'settings.messages.support_greeting': 'Support greeting',
  'settings.messages.support_greeting_hint': 'Prefilled when a shopper taps the chat button.',
  'settings.messages.buy_prefill': 'Buy on WhatsApp message',
  'settings.messages.buy_prefill_hint': '{{product_name}} is replaced with the product.',
  'settings.messages.default_language': 'Default language',
  'settings.messages.too_long': 'Keep it under {max} characters.',
  'settings.bot.title': 'Shopping assistant',
  'settings.bot.description': 'What happens automatically inside a chat.',
  'settings.bot.bot_enabled': 'Shopping bot',
  'settings.bot.bot_enabled_hint': 'Menus, product browsing and checkout inside the chat.',
  'settings.bot.ai_assistant_enabled': 'AI sales assistant',
  'settings.bot.ai_assistant_enabled_hint': 'Answers free-text questions using only your catalog.',
  'settings.bot.ai_locked': 'The AI assistant is not included in your plan.',
  'settings.bot.order_updates_enabled': 'Order updates',
  'settings.bot.order_updates_enabled_hint': 'Confirmation, shipping and delivery messages.',
  'settings.bot.catalog_auto_sync': 'Keep the catalog in sync',
  'settings.bot.catalog_auto_sync_hint': 'Product changes are pushed to WhatsApp as they happen.',
  'settings.bot.abandoned_cart_delay': 'Abandoned cart reminder after (minutes)',
  'settings.bot.abandoned_cart_delay_hint': 'Between 15 minutes and 24 hours.',
  'settings.bot.abandoned_cart_delay_invalid': 'Enter a number from 15 to 1440.',
  'settings.bot.catalog_message_enabled': 'Offer the WhatsApp catalog',
  'settings.bot.catalog_message_enabled_hint':
    'Sends Meta’s “View catalog” message with the menu once your products are synced, so customers browse and build a cart natively.',
  'settings.payments.title': 'Payments in WhatsApp',
  'settings.payments.description':
    'India only. Customers pay a bill inside the chat through the payment configuration you created in WhatsApp Manager, instead of a link to the storefront.',
  'settings.payments.enabled': 'Collect payments in the chat',
  'settings.payments.enabled_hint':
    'Needs an Indian number, prices in rupees and a payment configuration below.',
  'settings.payments.configuration': 'Payment configuration name',
  'settings.payments.configuration_hint':
    'WhatsApp Manager → Payments → the configuration linked to your Razorpay account.',
  'settings.payments.configuration_required': 'Enter the configuration name to collect payments.',
  'settings.data.title': 'Data retention',
  'settings.data.description': 'How long chat messages and the files customers send are kept.',
  'settings.data.retention': 'Delete messages after (days)',
  'settings.data.retention_hint': '0 keeps everything. Otherwise at least 30 days.',
  'settings.data.retention_invalid': 'Enter 0, or a number from 30 to 3650.',
  'settings.quick_replies.title': 'Quick replies',
  'settings.quick_replies.description': 'Canned answers your team can send from the inbox.',
  'settings.quick_replies.reply_title': 'Title',
  'settings.quick_replies.body': 'Message',
  'settings.quick_replies.add': 'Add a quick reply',
  'settings.quick_replies.remove': 'Remove',
  'settings.quick_replies.empty': 'No quick replies yet.',
  'settings.quick_replies.incomplete': 'Every quick reply needs a title and a message.',
  'settings.danger.title': 'Danger zone',
  'settings.danger.description': 'Disconnecting stops every message. Chats and customers are kept.',
  'settings.danger.disconnect': 'Disconnect WhatsApp',
  'settings.save': 'Save changes',
  'settings.saving': 'Saving…',
  'settings.saved': 'Settings saved.',
  'settings.unsaved': 'Unsaved changes',

  // --- sidebar status dot --------------------------------------------------
  'nav_status.CONNECTED': 'Connected',
  'nav_status.ATTENTION': 'Needs attention',
  'nav_status.NOT_CONNECTED': 'Not connected',

  // --- F1b section below ---------------------------------------------------

  // --- inbox ---------------------------------------------------------------
  'inbox.search_label': 'Search conversations',
  'inbox.search_placeholder': 'Search by name or phone',
  'inbox.filter.aria_label': 'Conversation filters',
  'inbox.filter.open': 'Open',
  'inbox.filter.unread': 'Unread',
  'inbox.filter.mine': 'Mine',
  'inbox.filter.closed': 'Closed',
  'inbox.filter.all': 'All',
  'inbox.list.aria_label': 'Conversations',
  'inbox.list.empty_title': 'No conversations yet',
  'inbox.list.empty_description': 'When a customer messages your WhatsApp number, the chat appears here.',
  'inbox.window_open': 'Reply window open',
  'inbox.window_closed': 'Reply window closed',
  'inbox.unread_count': '{count} unread',
  'inbox.bot_paused': 'Bot paused',

  'inbox.thread.aria_label': 'Messages',
  'inbox.thread.select_title': 'Pick a conversation',
  'inbox.thread.select_description': 'Choose a chat on the left to read it and reply.',
  'inbox.thread.back': 'Back to conversations',
  'inbox.thread.details': 'Details',
  'inbox.thread.closed_badge': 'Closed',
  'inbox.thread.load_older': 'Load older messages',
  'inbox.thread.empty': 'No messages in this conversation yet.',

  'inbox.message.status.QUEUED': 'Queued',
  'inbox.message.status.SENT': 'Sent',
  'inbox.message.status.DELIVERED': 'Delivered',
  'inbox.message.status.READ': 'Read',
  'inbox.message.status.FAILED': 'Failed',
  'inbox.message.failed': 'Not delivered: {error}',
  'inbox.message.image': 'Photo',
  'inbox.message.document': 'Document',
  'inbox.message.audio': 'Voice message',
  'inbox.message.video': 'Video',
  'inbox.message.template': 'Template · {name}',
  'inbox.message.product': 'Product',
  'inbox.message.product_list': 'Products',
  'inbox.message.order': 'Order',
  'inbox.message.system': 'System message',
  'inbox.message.unsupported': 'Unsupported message ({type})',
  'inbox.message.you': 'You',
  'inbox.message.automation': 'Automation',
  'inbox.message.bot': 'Shop assistant',

  'inbox.composer.label': 'Reply',
  'inbox.composer.placeholder': 'Type a reply…',
  'inbox.composer.placeholder_closed': 'Only approved templates can be sent right now',
  'inbox.composer.send': 'Send',
  'inbox.composer.hint': 'Enter to send · Shift+Enter for a new line',
  'inbox.composer.quick_replies': 'Quick replies',
  'inbox.composer.no_quick_replies': 'No quick replies yet. Add them under Settings.',
  'inbox.composer.attach': 'Attach',
  'inbox.composer.attach_image': 'Image by URL',
  'inbox.composer.attach_document': 'Document by URL',
  'inbox.composer.image_url': 'Image URL',
  'inbox.composer.document_url': 'Document URL',
  'inbox.composer.filename': 'File name',
  'inbox.composer.caption': 'Caption (optional)',
  'inbox.composer.send_product': 'Send product',
  'inbox.composer.send_template': 'Send template',
  'inbox.composer.send_order': 'Send order',
  'inbox.composer.select_order': 'Order',
  'inbox.composer.select_order_placeholder': 'Choose an order',
  'inbox.composer.no_orders': 'This customer has no orders to send.',
  'inbox.composer.window_closed_title': 'The 24-hour reply window has closed.',
  'inbox.composer.window_closed_body':
    'WhatsApp only allows approved templates until the customer writes again. Use “Send template”.',
  'inbox.composer.read_only': 'You can read this conversation but not reply.',

  'inbox.picker.product_title': 'Send a product',
  'inbox.picker.product_search': 'Search products',
  'inbox.picker.product_empty': 'No active products match.',
  'inbox.picker.product_send': 'Send product',
  'inbox.picker.product_variant': 'Variant',
  'inbox.picker.product_any_variant': 'Let the customer choose',
  'inbox.picker.product_message': 'Message (optional)',
  'inbox.picker.product_message_placeholder': 'Here is the one we spoke about.',
  'inbox.picker.template_title': 'Send a template',
  'inbox.picker.template_select': 'Template',
  'inbox.picker.template_choose': 'Choose a template',
  'inbox.picker.template_empty': 'No approved templates yet. Create and submit one under Templates.',
  'inbox.picker.template_variables': 'Fill in the details',
  'inbox.picker.template_send': 'Send template',

  'inbox.context.title': 'Details',
  'inbox.context.none': 'Open a conversation to see who you are talking to.',
  'inbox.context.customer': 'Customer',
  'inbox.context.no_customer': 'Not linked to a customer record yet. One is created at their first order.',
  'inbox.context.view_customer': 'View customer',
  'inbox.context.orders_count': '{count} orders',
  'inbox.context.spent': '{amount} spent',
  'inbox.context.opt_in': 'Marketing opt-in',
  'inbox.context.opt_in_hint': 'Switch on only when the customer has agreed to promotional messages.',
  'inbox.context.window_expires': 'Reply window open until {time}',
  'inbox.context.actions': 'Conversation',
  'inbox.context.assign_me': 'Assign to me',
  'inbox.context.unassign': 'Unassign',
  'inbox.context.assigned_to': 'Assigned to {name}',
  'inbox.context.close': 'Close',
  'inbox.context.reopen': 'Reopen',
  'inbox.context.pause_bot': 'Pause bot',
  'inbox.context.resume_bot': 'Resume bot',
  'inbox.context.product': 'Looking at',
  'inbox.context.cart': 'Cart',
  'inbox.context.cart_empty': 'No items in the cart.',
  'inbox.context.cart_total': 'Total',
  'inbox.context.recent_orders': 'Recent orders',
  'inbox.context.no_orders': 'No orders yet.',
  'inbox.errors.generic': 'Something went wrong. Please try again.',
  'inbox.errors.not_found': 'This conversation could not be loaded.',

  // --- templates -----------------------------------------------------------
  'templates.title': 'Message templates',
  'templates.subtitle': 'Pre-approved messages you can send any time, even after the 24-hour window.',
  'templates.new': 'New template',
  'templates.seed': 'Add default templates',
  'templates.sync': 'Sync statuses',
  'templates.filter.status': 'Status',
  'templates.filter.category': 'Category',
  'templates.filter.all': 'All',
  'templates.status.DRAFT': 'Draft',
  'templates.status.SUBMITTED': 'Submitted',
  'templates.status.APPROVED': 'Approved',
  'templates.status.REJECTED': 'Rejected',
  'templates.status.DISABLED': 'Disabled',
  'templates.status.PAUSED': 'Paused',
  'templates.category.MARKETING': 'Marketing',
  'templates.category.UTILITY': 'Utility',
  'templates.category.AUTHENTICATION': 'Authentication',
  'templates.button_type.QUICK_REPLY': 'Quick reply',
  'templates.button_type.URL': 'Visit website',
  'templates.button_type.PHONE_NUMBER': 'Call',
  'templates.variables.customer_name': 'Customer name',
  'templates.variables.order_number': 'Order number',
  'templates.variables.order_total': 'Order total',
  'templates.variables.tracking_url': 'Tracking link',
  'templates.variables.store_name': 'Store name',
  'templates.variables.cart_url': 'Cart link',
  'templates.empty_title': 'No templates yet',
  'templates.empty_description': 'Start from the defaults for order updates, or write your own.',
  'templates.card.builtin': 'Default',
  'templates.card.rejected': 'Rejected by Meta: {reason}',
  'templates.card.approved_at': 'Approved {time}',
  'templates.card.submitted_at': 'Submitted {time}',
  'templates.card.not_submitted': 'Not submitted yet',
  'templates.card.edit': 'Edit',
  'templates.card.submit': 'Submit to Meta',
  'templates.card.delete': 'Delete',
  'templates.card.delete_label': 'Delete {name}',
  'templates.confirm.submit_title': 'Submit to Meta?',
  'templates.confirm.submit_body':
    '“{name}” will be sent to Meta for review. It cannot be edited until Meta answers, usually within a day.',
  'templates.confirm.submit_confirm': 'Submit',
  'templates.confirm.delete_title': 'Delete template?',
  'templates.confirm.delete_body':
    '“{name}” will be removed here and at Meta. Automations using it will stop sending.',
  'templates.confirm.delete_confirm': 'Delete',
  'templates.form.create_title': 'New template',
  'templates.form.edit_title': 'Edit template',
  'templates.form.name': 'Name',
  'templates.form.name_hint': 'Lowercase letters, numbers and underscores.',
  'templates.form.name_error': 'Use only lowercase letters, numbers and underscores.',
  'templates.form.language': 'Language',
  'templates.form.category': 'Category',
  'templates.form.header_format': 'Header',
  'templates.form.header_none': 'No header',
  'templates.form.header_text': 'Header text',
  'templates.form.header_text_required': 'Add the header text.',
  'templates.form.header_media': 'Header file',
  'templates.form.header_media_choose': 'Choose a file',
  'templates.form.header_media_replace': 'Replace',
  'templates.form.header_media_remove': 'Remove',
  'templates.form.header_media_uploading': 'Uploading…',
  'templates.form.header_media_hint_IMAGE': 'JPEG or PNG, up to {max} MB.',
  'templates.form.header_media_hint_VIDEO': 'MP4, up to {max} MB.',
  'templates.form.header_media_hint_DOCUMENT': 'PDF, up to {max} MB.',
  'templates.form.header_media_required': 'Upload the file shown at the top of the message.',
  'templates.form.header_media_failed': 'Could not upload that file. Please try again.',
  'templates.form.header_media_document': 'Document',
  'templates.form.body': 'Body',
  'templates.form.body_hint': '{length}/{max} characters',
  'templates.form.body_required': 'Write the message body.',
  'templates.form.body_too_long': 'Keep the body under {max} characters.',
  'templates.form.insert_variable': 'Insert variable',
  'templates.form.footer': 'Footer (optional)',
  'templates.form.footer_hint': 'Up to {max} characters.',
  'templates.form.footer_too_long': 'Keep the footer under {max} characters.',
  'templates.form.buttons': 'Buttons',
  'templates.form.add_button': 'Add button',
  'templates.form.remove_button': 'Remove button',
  'templates.form.button_type': 'Type',
  'templates.form.button_text': 'Label',
  'templates.form.button_url': 'URL',
  'templates.form.button_phone': 'Phone number',
  'templates.form.buttons_limit': 'Up to three buttons, at most two of them links.',
  'templates.form.buttons_invalid':
    'Every button needs a label of up to 25 characters; link and call buttons need a valid URL or number.',
  'templates.form.save': 'Save draft',
  'templates.preview.title': 'Preview',
  'templates.preview.empty_body': 'Your message will appear here.',
  'templates.preview.sample_hint': 'Placeholders are shown with sample values.',
  'templates.preview.button_placeholder': 'Button',
  'templates.notice.created': 'Template saved as a draft.',
  'templates.notice.updated': 'Template updated.',
  'templates.notice.submitted': 'Submitted to Meta. Sync statuses later to see the verdict.',
  'templates.notice.deleted': 'Template deleted.',
  'templates.notice.sync_started': 'Syncing statuses with Meta…',
  'templates.notice.seeded': 'Default templates added as drafts.',
  'templates.errors.generic': 'Something went wrong. Please try again.',

  // --- automations ---------------------------------------------------------
  'automations.title': 'Automations',
  'automations.subtitle': 'Messages your shop sends by itself when something happens.',
  'automations.list_label': 'Automations',
  'automations.some_locked': 'Some automations are not included in your plan.',
  'automations.no_templates':
    'No approved templates yet. Until one is approved, automations can only reach customers who wrote in the last 24 hours.',
  'automations.manage_templates': 'Manage templates',
  'automations.notice.saved': '{name} saved.',
  'automations.trigger.WELCOME.label': 'Welcome',
  'automations.trigger.WELCOME.description': 'Greets a customer the first time they message you.',
  'automations.trigger.ORDER_CONFIRMATION.label': 'Order confirmation',
  'automations.trigger.ORDER_CONFIRMATION.description': 'Sent when an order is placed.',
  'automations.trigger.PAYMENT_CONFIRMATION.label': 'Payment received',
  'automations.trigger.PAYMENT_CONFIRMATION.description': 'Sent when an online payment is captured.',
  'automations.trigger.ORDER_PROCESSING.label': 'Order processing',
  'automations.trigger.ORDER_PROCESSING.description': 'Sent when you start preparing the order.',
  'automations.trigger.ORDER_SHIPPED.label': 'Order shipped',
  'automations.trigger.ORDER_SHIPPED.description':
    'Sent when the order leaves, with the tracking link if there is one.',
  'automations.trigger.ORDER_DELIVERED.label': 'Order delivered',
  'automations.trigger.ORDER_DELIVERED.description': 'Sent when the order is marked delivered.',
  'automations.trigger.ORDER_CANCELLED.label': 'Order cancelled',
  'automations.trigger.ORDER_CANCELLED.description': 'Sent when an order is cancelled.',
  'automations.trigger.REFUND_PROCESSED.label': 'Refund processed',
  'automations.trigger.REFUND_PROCESSED.description': 'Sent when a refund is issued.',
  'automations.trigger.ABANDONED_CART.label': 'Abandoned cart',
  'automations.trigger.ABANDONED_CART.description':
    'Nudges an opted-in customer who left items in their cart.',
  'automations.trigger.APPOINTMENT_CONFIRMED.label': 'Appointment confirmed',
  'automations.trigger.APPOINTMENT_CONFIRMED.description':
    'Sent when an appointment is booked or confirmed, to a customer who agreed to WhatsApp updates.',
  'automations.trigger.APPOINTMENT_REMINDER.label': 'Appointment reminder',
  'automations.trigger.APPOINTMENT_REMINDER.description':
    'Reminds a customer who agreed to WhatsApp updates ahead of their appointment. Timing is set in Bookings → Reminders.',
  'automations.trigger.APPOINTMENT_CANCELLED.label': 'Appointment cancelled',
  'automations.trigger.APPOINTMENT_CANCELLED.description':
    'Tells a customer who agreed to WhatsApp updates that their appointment was cancelled.',
  'automations.trigger.TOKEN_ISSUED.label': 'Token issued',
  'automations.trigger.TOKEN_ISSUED.description':
    'Sends a patient who agreed to WhatsApp updates their token number and estimated time when they join a token queue.',
  'automations.trigger.TOKEN_NEAR.label': 'Your turn is near',
  'automations.trigger.TOKEN_NEAR.description':
    'Tells a patient who agreed to WhatsApp updates that their token is coming up soon, so they can make their way in.',
  'automations.row.enabled': 'Enable {name}',
  'automations.row.template': 'Template',
  'automations.row.template_none': 'None — plain message inside the 24-hour window',
  'automations.row.requires_template': 'This automation needs an approved template.',
  'automations.row.requires_template_hint':
    'Customers rarely write just before this fires, so a template is needed to reach them.',
  'automations.row.message': 'Message',
  'automations.row.message_hint':
    'Used when no template is chosen and the customer wrote within 24 hours. The same placeholders as templates work here.',
  'automations.row.delay': 'Wait (minutes)',
  'automations.row.delay_hint': 'How long after the last message before the nudge goes out.',
  'automations.row.delay_error': 'Between {min} and {max} minutes.',
  'automations.row.save': 'Save',
  'automations.row.preview': 'Preview',
  'automations.row.locked': 'Not included in your plan.',
  'automations.row.upgrade': 'See plans',
  'automations.row.sent_30d': '{count} sent in the last 30 days',
  'automations.row.last_run': 'last {time}',
  'automations.preview.title': 'Preview',
  'automations.logs.reason.automation_disabled': 'Skipped: this automation is switched off.',
  'automations.logs.reason.customer_blocked': 'Skipped: the customer has blocked your number.',
  'automations.logs.reason.no_whatsapp_customer': 'Skipped: the customer has no WhatsApp number.',
  'automations.logs.reason.not_connected': 'Skipped: WhatsApp is not connected.',
  'automations.logs.reason.not_opted_in': 'Skipped: the customer has not agreed to WhatsApp messages.',
  'automations.logs.reason.plan_not_included': 'Skipped: your plan does not include WhatsApp automations.',
  'automations.logs.reason.sending_paused': 'Skipped: sending is paused.',
  'automations.logs.reason.template_required': 'Skipped: an approved template is needed outside the 24-hour window.',
  'automations.logs.reason.window_closed': 'Skipped: the 24-hour reply window has closed.',
  'automations.logs.title': 'Activity',
  'automations.logs.filter': 'Filter by trigger',
  'automations.logs.filter_all': 'All triggers',
  'automations.logs.empty': 'Nothing sent yet.',
  'automations.logs.columns.when': 'When',
  'automations.logs.columns.trigger': 'Trigger',
  'automations.logs.columns.status': 'Status',
  'automations.logs.columns.order': 'Order',
  'automations.logs.columns.detail': 'Detail',
  'automations.logs.conversation': 'Open chat',
  'automations.logs.page': 'Page {page} of {pages}',
  'automations.status.SENT': 'Sent',
  'automations.status.SKIPPED': 'Skipped',
  'automations.status.FAILED': 'Failed',
  'automations.errors.generic': 'Something went wrong. Please try again.',
};

export default whatsappChannel;
